import React, { useState, useEffect, useCallback } from 'react';
import AntTable from 'common_components/AntTable';
import EditButtonRenderer from 'common_components/EditButtonRenderer';
import LinkRenderer from 'common_components/LinkRenderer';
import { dateTimeTimestamptzFormatter } from 'common_components/AntTable/tableUtils';
import TableToolbar from 'common_components/AntTable/Toolbar';
import { push } from 'connected-react-router';
import permission from 'permissions';
import { useSelector, useDispatch } from 'react-redux';
import { getCurrentUser } from 'reducers/user';
import { useDeleteTrailerMutation } from 'reducers/masterData/trailer/api';
import { useErrorHandler } from 'common_components/ErrorContext';
import { Sorter } from 'utils/sorter';
import styled from 'styled-components';
import { selectTableStateById } from 'reducers/table';
import { message } from 'antd';

const TrailerTableComponent = (props) => {
  const { rowData, isLoading } = props;
  const [dataSource, setDataSource] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const tableIdSelector = useSelector((state) =>
    selectTableStateById(state, 'trailer')
  );

  const [showDelete, setShowDelete] = useState(false);

  const dispatch = useDispatch();
  const { setErrorData } = useErrorHandler();
  const user = useSelector(getCurrentUser);

  const [columns, setColumns] = useState(() => {
    let cols = [
      {
        title: '',
        dataIndex: 'edit',
        align: 'center',
        render: (text, record) => {
          return (
            <EditButtonRenderer data={record} onClick={handleEditClicked} />
          );
        },
        width: 50,
      },
      {
        title: 'BZP',
        dataIndex: 'bzp',
        render: (text, record) => {
          return text && text.job_name
        },
        width: 200,
        sorter: {
          compare: Sorter.DEFAULT,
          field: 'job_name',
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'Trailer Type',
        dataIndex: 'trailer_type',
        render: (text, record) => {
          return (
            <LinkRenderer
              data={record}
              field='trailer_type'
              name='name'
              path='/app/master/trailers/trailer_types'
            />
          );
        },
        width: 200,
        sorter: {
          compare: Sorter.DEFAULT,
          field: 'name',
        },
        movable: true,
        searchable: true,
      },
      // {
      //   title: 'Trailer loading',
      //   dataIndex: 'trailer_loading',
      //   width: 150,
      //   sorter: {
      //     compare: Sorter.DEFAULT,
      //   },
      //   movable: true,
      //   searchable: true,
      // },
      {
        title: 'License Plate Head',
        dataIndex: 'license_plate_head',
        width: 150,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'License Plate Tail',
        dataIndex: 'license_plate_tail',
        width: 150,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'Trailer Age',
        dataIndex: 'trailer_age',
        width: 150,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'Trailer Card ID',
        dataIndex: 'trailer_card_id',
        width: 150,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'GPS Code',
        dataIndex: 'trailer_gps_code',
        width: 150,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'CNG Allowance',
        dataIndex: 'cng_allowance',
        width: 150,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'Is Active',
        dataIndex: 'is_active',
        width: 100,
        render: (text, record) => {
          return text.toString();
        },
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
      },
      {
        title: 'Updated time',
        dataIndex: 'updated_at',
        render: (text, record) => {
          return dateTimeTimestamptzFormatter({ value: text });
        },
        sorter: {
          compare: Sorter.DATE,
        },
        movable: true,
      },
      {
        title: 'Created time',
        dataIndex: 'created_at',
        render: (text, record) => {
          return dateTimeTimestamptzFormatter({ value: text });
        },
        sorter: {
          compare: Sorter.DATE,
        },
        movable: true,
      },
    ];

    // Add Show/Hide status from Redux store
    if (tableIdSelector) {
      cols = cols.map((c) => ({
        ...c,
        ...tableIdSelector.find((t) => t.dataIndex === c.dataIndex),
      }));
    }

    return cols;
  });

  useEffect(() => {
    setDataSource(rowData);
  }, [rowData]);

  const [
    deleteTrailer,
    {
      isSuccess: isSuccessDeleting,
      isUninitialized: isUninitDeleting,
      error: errorDeleting,
      isLoading: isDeleting,
    },
  ] = useDeleteTrailerMutation();

  useEffect(() => {
    if (errorDeleting) {
      setErrorData(errorDeleting);
    }
  }, [errorDeleting, setErrorData]);

  useEffect(() => {
    if (!isUninitDeleting && isSuccessDeleting) {
      message.success('Data was successfully deleted');
      clearSelectedRows();
    }
  }, [isSuccessDeleting, isUninitDeleting]);

  const onSearch = useCallback(
    (searchTerm) => {
      if (rowData && rowData.length > 0) {
        let searchCols = columns.filter((c) => c.searchable);
        if (searchCols.length > 0 && searchTerm) {
          let filterData = rowData.filter((d) =>
            searchCols.some(
              (s) =>
                d[s.dataIndex] &&
                d[s.dataIndex]
                  .toString()
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase())
            )
          );
          setDataSource(filterData);
        } else {
          setDataSource(rowData);
        }
      }
      clearSelectedRows();
    },
    [columns, rowData]
  );

  const onCreate = () => {
    dispatch(push(`/app/master/trailers/create`));
  };

  const handleEditClicked = (cell, readOnly) => {
    dispatch(push(`/app/master/trailers/${cell.id}`));
  };

  const onDelete = () => {
    // TODO: Bulk delete
    for (let row of selectedRows) {
      deleteTrailer({ id: row.id });
    }

    clearSelectedRows();
  };

  const onReload = () => {
    props.refetchData();
    clearSelectedRows();
  };

  const clearSelectedRows = () => {
    setSelectedRows([]);
    setSelectedRowKeys([]);
    setShowDelete(false);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows);

      if (permission.checkPermission(user, 'trailer', ['d'])) {
        setShowDelete(selectedRows.length > 0 ? true : false);
      }
    },
  };

  return (
    <Container>
      <TableToolbar
        onCreate={onCreate}
        onDelete={onDelete}
        onReload={onReload}
        showDelete={showDelete}
        showReload={true}
        isLoading={isLoading}
        isDeleting={isDeleting}
        showAdd={permission.checkPermission(user, 'trailer', ['c'])}
        showSearch={true}
        columns={columns}
        setColumns={setColumns}
        onSearch={onSearch}
      />
      <AntTable
        rowKey='id'
        tableId={'trailer'}
        bordered
        rowSelection={rowSelection}
        showSorterTooltip={false}
        loading={isLoading || isDeleting}
        columns={columns}
        dataSource={dataSource}
        scroll={{ x: 'max-content' }}
        // Below is for right click context menu
        showContextMenu={true}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        menuActions={[
          { label: 'Edit', action: (row) => handleEditClicked(row) },
          {
            label: 'Delete',
            action: (row) => deleteTrailer({ id: row.id }),
            delete: true,
          },
        ]}
      />
    </Container>
  );
};

const Container = styled.div`
  padding: 0 10px;
`;

export default TrailerTableComponent;
